import React from 'react';
import { Text, Spinner, Tooltip, mergeClasses } from '@fluentui/react-components';
import { EditRegular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { BasicButton } from '@components/common/BasicButton';
import { useReviewSectionStyles } from './ReviewSection.styles';
import type { ReviewSectionState } from '@store/models/booking';

export interface ReviewItem {
  label: string;
  value: React.ReactNode;
}

export interface ReviewSectionProps {
  title: string;
  icon?: React.ReactNode;
  items?: ReviewItem[];
  columns?: 2 | 3;
  onEdit?: () => void;
  className?: string;
  children?: React.ReactNode;
  sectionState?: ReviewSectionState;
  loading?: boolean;
}

export const ReviewSection: React.FC<ReviewSectionProps> = ({
  title,
  icon,
  items,
  columns = 2,
  onEdit,
  className,
  children,
  sectionState = 'completed',
  loading = false,
}) => {
  const { t } = useTranslation('bookingForm');
  const styles = useReviewSectionStyles();

  const isLocked = sectionState === 'locked';
  const isActionable = sectionState === 'actionable';

  const rootClass = mergeClasses(
    styles.root,
    isActionable && styles.actionable,
    isLocked && styles.locked,
    className
  );

  const editButton = onEdit && (
    <Tooltip
      content={
        isLocked
          ? t('bookingForm.review.sectionLocked')
          : `${t('bookingForm.buttons.edit')} ${title}`
      }
      relationship="label"
    >
      <BasicButton
        variant="outline"
        size="small"
        icon={<EditRegular />}
        className={styles.editButton}
        onClick={isLocked ? undefined : onEdit}
        disabled={isLocked}
        aria-disabled={isLocked}
        aria-label={`${t('bookingForm.buttons.edit')} ${title}`}
      >
        {t('bookingForm.buttons.edit')}
      </BasicButton>
    </Tooltip>
  );

  return (
    <section className={rootClass} aria-label={title}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          {icon && <span className={styles.headerIcon}>{icon}</span>}
          <Text as="h3" className={styles.title}>
            {title}
          </Text>
        </div>
        {editButton}
      </div>
      {loading ? (
        <Spinner
          size="small"
          label={t('bookingForm.messages.loading', { defaultValue: 'Loading...' })}
        />
      ) : (
        <>
          {items && items.length > 0 && (
            <div className={mergeClasses(styles.grid, columns === 3 ? styles.grid3 : styles.grid2)}>
              {items.map((item) => (
                <div key={item.label} className={styles.cell}>
                  <Text className={styles.label}>{item.label}</Text>
                  <Text className={styles.value}>{item.value ?? '—'}</Text>
                </div>
              ))}
            </div>
          )}
          {children}
        </>
      )}
    </section>
  );
};

ReviewSection.displayName = 'ReviewSection';
